import { CheckIcon, CloseIcon, DeleteIcon, DragHandleIcon, EditIcon } from '@chakra-ui/icons'
import {
  Box,
  Center,
  Checkbox as ChakraCheckbox,
  Collapse,
  Divider,
  Flex,
  FlexProps,
  Grid,
  HeadingProps,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  StackProps,
  Text,
  TextProps,
  Tooltip,
  TooltipProps,
  VStack,
} from '@chakra-ui/react'
import {
  ALPHABET,
  AlternateField,
  colors,
  defaultValidation,
  FieldCondition,
  fieldFormat,
  FieldFormatter,
  FieldMap,
  FieldMapValue,
  fieldParse,
  FieldTypes,
  FileDBValue,
  FileField,
  FileMimeType,
  getDateTimeString,
  getFieldIsRedFlagged,
  Field as IField,
  FormElement as IFormElement,
  InfoStage,
  InputSize,
  isField,
  isFieldValueEmpty,
  isInfoStage,
  isListField,
  ListField,
  mergeData,
  ShareCorrections,
  SignatureField,
  UnuploadedFileDBValue,
  UpdateCallback,
  UploadProgress,
} from '@hb/shared'
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { ValidationErrors } from 'final-form'
import arrayMutators from 'final-form-arrays'
import React, {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  ReactNode,
  Ref,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Field, Form, FormRenderProps, FormSpy, useForm } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'

import { makePlural } from '@chem-po/core'
import { useFirebase } from '@chem-po/firebase-web'
import {
  EditableStateContext,
  PopUpMessageContext,
  ScreenContext,
  ThemeContext,
  ThemeContextData,
  useApp,
  useAuth,
  useCollections,
  useFilePath,
  useFormattedValue,
} from '@hb/react'
import { get as nestedGet } from 'nested-property'
import { processFileFieldData } from '../../../backend/db/utils/processData'
import { deleteFile } from '../../../backend/storage/utils'
import { useFile, UseFileData } from '../../../hooks/backend/storage/downloadFile'
import { getSelectedItem } from '../../../hooks/backend/useSelectedItem'
import { SvgIcon } from '../../../icons/SvgIcon'
import uploadIcon from '../../../icons/upload.svg'
import {
  EditableProps,
  FormElementProps,
  InputElement as IInputElement,
  InputProps,
  ListInputProps,
} from '../../../types/fields'
import { ActionButton, RoundedButton } from '../../Buttons/ActionButton'
import { DeleteButton } from '../../Buttons/DeleteButton'
import { HeaderButton } from '../../Buttons/HeaderButton'
import { ViewButton } from '../../Buttons/ViewButton'
import { CollapseError } from '../../CollapseError'
import { CollapseWarning } from '../../CollapseWarning'
import { Container } from '../../Container'
import { DataViewContext, useCorrections, useShareCorrections } from '../../DataView/context'
import { DataCell } from '../../DataView/DataCell'
import { ProfileRedFlagPopover } from '../../DataView/RedFlagPopover'
import { ExpandOnMount } from '../../ExpandOnMount'
import { Header } from '../../Header'
import { UserBadge } from '../../Users/UserBadge'
import { DBTextStage } from '../DBTextStage'
import { Condition } from '../FinalForm/Condition'
import { useFormWidth } from '../FinalForm/context'
import usePopulatedHints from '../FinalForm/hooks/usePopulatedHints'
import usePopulatedLabels from '../FinalForm/hooks/usePopulatedLabels'
import { UploadProgressView } from '../FinalForm/UploadProgressView'
import { AlternateValue, AlternateValuePopover } from './AlternateValuePopover'
import { BloodPressureInput } from './BloodPressure/BloodPressureInput'
import Checkbox from './Checkbox/Checkbox'
import { DataLabel } from './DataLabel'
import { DateInput } from './Date/DateInput'
import { DateTimeInput } from './Date/DateTimeInput'
import { TimeInput } from './Date/TimeInput'
import BooleanDropdown from './Dropdown/BooleanDropdown'
import DatabaseDropdown from './Dropdown/DatabaseDropdown'
import DynamicDropdown from './Dropdown/DynamicDropdown'
import { EditableCheckbox } from './EditableCheckbox'
import { ExamSectionInput } from './ExamSection/ExamSectionInput'
import { BaseFileView } from './File/BaseFileView'
import { ChangedFile, useUploadDbFileValue } from './File/hooks'
import Hints from './Hints'
import Labels from './Labels'
import { MultipleSelectInput } from './MultipleSelect'
import { HeightInput } from './Number/HeightInput'
import { NumberInput } from './Number/NumberInput'
import { Presets } from './Presets'
import { DropdownInput } from './Select/DropdownInput'
import { getInputSizeTokens, useInputSize } from './Shared/sizes'
import InitialsInput, { INITIALS_CONTAINER_WIDTH } from './Signatures/InitialsInput'
import { SignatureInputBody } from './Signatures/SignatureInputBody'
import DollarAmountInput from './Text/DollarAmount/DollarAmountInput'
import EmailInput from './Text/Email/EmailInput'
import PercentageInput from './Text/Percentage/PercentageInput'
import PhoneInput from './Text/Phone/PhoneInput'
import TextArea from './Text/TextArea/TextArea'
import TextInput from './Text/TextInput'
import { InputRef } from './types'

type OnFileViewChange =
  | ((value: FileDBValue | null) => Promise<UpdateCallback>)
  | ((value: FileDBValue | null) => void)
interface FileViewProps {
  field: FileField | null
  small?: boolean
  noDelete?: boolean
  header?: ReactNode
  onFileUpload?: (value: React.ChangeEvent<HTMLInputElement>) => Promise<void> | void
  onUploadMultiple?: (value: React.ChangeEvent<HTMLInputElement>) => Promise<void> | void
  onChange?: OnFileViewChange
  onBlur?: () => void
  noFullView?: boolean
  onFocus?: () => void
  data?: FileDBValue | UnuploadedFileDBValue
  flexProps?: FlexProps
  withDownload?: boolean
  children?: React.ReactNode
  isLoading?: boolean
}
// checks if [index] is in string, e.g. [0] or [1]
const containsIndexRegex = /\[\d+\]/
const isInList = (name: string) => containsIndexRegex.exec(name)
// with new system, we should not need to pass field to FileView as storagePath is in value
const PreFileView = (
  {
    field,
    data,
    flexProps,
    small,
    noDelete,
    onUploadMultiple,
    onFileUpload,
    onChange,
    withDownload,
    header,
    isLoading: isLoadingProp,
    children,
    noFullView,
  }: FileViewProps,
  ref: Ref<InputRef>,
) => {
  const { placeholder, fileType } = field ?? {}
  const { showError, showSuccess } = useContext(PopUpMessageContext)
  const { theme } = useContext(ThemeContext)
  const [updatedUrl, setUpdatedUrl] = useState<string | null>(null)
  const [updatedMetadata, setUpdatedMetadata] = useState<any | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const { downloadPath, recentlyUploaded } = useFilePath(data)

  const fileArgs = useMemo<UseFileData>(
    () => ({
      path: downloadPath,
      value: data,
    }),
    [downloadPath, data],
  )

  const { metadata, loading, error, url, blob, blobErr, blobLoading } = useFile(fileArgs)

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) inputRef.current.click()
    },
    blur: () => {
      if (inputRef.current) inputRef.current.blur()
    },
  }))

  const [isDeleting, setIsDeleting] = useState(false)

  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const handleDelete = useCallback(async () => {
    if (!onChange) return
    setIsDeleting(true)
    if (downloadPath) {
      const deleteFileRes = await deleteFile(downloadPath)
      if (deleteFileRes.error) showError(deleteFileRes.error)
      if (deleteFileRes.success) {
        setUpdatedUrl('')
        const res = await onChange(null)
        if (res?.error) showError(res.error)
        else {
          setUpdatedMetadata({})
          showSuccess('Successfully deleted file!')
        }
      }
    }
    if (mounted.current) {
      setIsDeleting(false)
    }
  }, [onChange, downloadPath, showError, showSuccess])

  const displayedUrl = useMemo(() => updatedUrl ?? url, [url, updatedUrl])

  const accept = useMemo(() => {
    switch (fileType) {
      case 'image':
        return 'image/png, image/jpeg'
      case 'pdf':
        return 'application/pdf'
      default:
        return 'image/png, image/jpeg, application/pdf'
    }
  }, [fileType])

  const { isMobile } = useContext(ScreenContext)

  const fileName = useMemo(() => {
    if (!data) return undefined
    const baseName = data.name ?? placeholder ?? ''
    const hasExt = baseName.includes('.')
    if (hasExt) return baseName
    const ext = FileMimeType[data.type as keyof typeof FileMimeType]
    return ext ? `${baseName}.${ext}` : baseName
  }, [data, placeholder])

  const displayedMetadata = useMemo(() => updatedMetadata || metadata, [updatedMetadata, metadata])

  const isLoading = useMemo(
    () => loading || blobLoading || recentlyUploaded || isLoadingProp,
    [loading, blobLoading, recentlyUploaded, isLoadingProp],
  )
  return (
    <Flex p={small ? 0 : 1} direction={['column', 'row']} w="100%" {...flexProps}>
      <Flex
        m={1}
        mr={1}
        w={isMobile ? '100%' : 'auto'}
        justify={isMobile ? 'center' : 'flex-start'}
        align="center">
        <BaseFileView
          blob={blob}
          small={small}
          fileName={fileName}
          error={error ?? blobErr}
          withDownload={withDownload}
          noFullView={noFullView}
          loading={isLoading}
          header={data?.name ?? placeholder}>
          {onChange ? null : children}
        </BaseFileView>
      </Flex>
      {small ? null : (
        <Flex
          ml={isMobile ? 0 : 'auto'}
          px={2}
          pt={0}
          pb={2}
          flex={1}
          direction="column"
          minW="0"
          align={isMobile ? 'flex-start' : 'flex-end'}>
          <Flex gap={1.5} w="100%">
            {header && !isEditing ? header : null}
            <Box flex={1} minW="0">
              {onChange && data ? (
                <Editable
                  onSubmit={async d => {
                    const changePromise = onChange({
                      ...(data as FileDBValue),
                      name: d,
                    })
                    if (changePromise) {
                      return changePromise
                    }
                    return { success: 'File name updated' }
                  }}
                  openCallback={() => {
                    setIsEditing(true)
                  }}
                  closeCallback={() => {
                    setIsEditing(false)
                  }}
                  theme={theme}
                  value={data?.name ?? placeholder}
                  style={{ width: '100%' }}
                  dataCellTooltip
                  dataCellProps={{
                    as: 'span',
                    display: 'block',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                  inputStyle={{
                    justifyContent: 'flex-end',
                    textAlign: 'right',
                  }}
                  field={{
                    type: FieldTypes.TEXT,
                    placeholder: placeholder ?? 'File Name',
                    optional: true,
                  }}
                />
              ) : (
                <Tooltip placement="top" hasArrow label={data?.name ?? placeholder}>
                  <DataCell
                    width="100%"
                    justifyContent="flex-end"
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap">
                    {data?.name ?? placeholder}
                  </DataCell>
                </Tooltip>
              )}
            </Box>
          </Flex>
          {/* <Text>{placeholder}</Text> */}
          <Divider mb={1} />
          {displayedMetadata?.updated || (data?.uploadedBy && data?.uploadedByGroup) ? (
            <Flex gap={1.5} align="center">
              {displayedMetadata?.updated ? (
                <Text textAlign={isMobile ? 'left' : 'right'} fontSize="sm">
                  <i>Uploaded {getDateTimeString(new Date(displayedMetadata.updated))}</i>
                </Text>
              ) : null}
              {data?.uploadedBy && data?.uploadedByGroup ? (
                <UserBadge userId={data.uploadedBy} userGroup={data.uploadedByGroup} />
              ) : null}
            </Flex>
          ) : null}
          {onChange && onFileUpload ? (
            <Flex gap={2} mt={2} w={isMobile ? '100%' : 'auto'} align="center">
              {children}
              <Box position="relative">
                <ActionButton
                  size="sm"
                  pl={2}
                  pr={3}
                  bg="white"
                  gap={1}
                  onClick={() => {
                    if (inputRef.current) inputRef.current.click()
                  }}>
                  <SvgIcon w="16px" src={uploadIcon} color={colors.green.hex} />
                  <Text>{displayedUrl ? 'Update' : 'Upload'}</Text>
                </ActionButton>
                <input
                  ref={inputRef}
                  style={{
                    marginLeft: 'auto',
                    opacity: 0,
                    width: 10,
                    position: 'absolute',
                  }}
                  onChange={async e => {
                    const newFiles = e.target.files ? Array.from(e.target.files) : []
                    if (!newFiles.length) return
                    if (newFiles.length > 1 && onUploadMultiple) {
                      await onUploadMultiple(e)
                    } else {
                      await onFileUpload(e)
                    }
                  }}
                  multiple={!!onUploadMultiple}
                  type="file"
                  accept={accept}
                />
              </Box>
              {(displayedUrl || data) && !noDelete ? (
                <IconButton
                  ml={isMobile ? 'auto' : 2}
                  colorScheme="red"
                  isLoading={isDeleting}
                  aria-label="delete"
                  icon={<DeleteIcon />}
                  onClick={handleDelete}
                  size="sm"
                />
              ) : null}
            </Flex>
          ) : null}
        </Flex>
      )}
    </Flex>
  )
}

export const FileView = forwardRef<InputRef, FileViewProps>(PreFileView)

// value is URL returned from firebase
const PreFileInput = (
  { field, input, disabled, readOnly }: InputProps<FileField>,
  ref: Ref<InputRef>,
) => {
  const { change } = useForm()
  // const [, setLoadingState] = useState<'uploading' | 'deleting' | null>(null)
  const { parentPath, fieldIndex } = useMemo(() => {
    if (!input?.name) return { parentPath: null, fieldIndex: null }
    const matches = isInList(input.name)
    if (!matches?.length) {
      return {
        parentPath: input.name.split('.').slice(0, -1).join('.'),
        fieldIndex: null,
      }
    }
    const asInt = parseInt(matches[0].slice(1, -1), 10)
    return {
      fieldIndex: Number.isNaN(asInt) ? null : asInt,
      parentPath: input.name.split('[')[0],
    }
  }, [input])
  const onSingleFileChange = useCallback(
    ([uploaded]: Array<ChangedFile>) => {
      if (uploaded) {
        input?.onChange(uploaded.file)
        input?.onBlur()
      }
    },
    [input],
  )
  const onFileUpload = useUploadDbFileValue(onSingleFileChange, undefined, input?.onFocus)

  const onMultipleFileChange = useCallback(
    (files: Array<ChangedFile>) => {
      if (!parentPath || fieldIndex === null) return
      if (files.length) {
        files.forEach((file, i) => {
          change(`${parentPath}[${fieldIndex + i}]`, file.file)
        })
      }
      input?.onBlur()
    },
    [parentPath, fieldIndex, change, input],
  )

  const handleUploadMultiple = useUploadDbFileValue(onMultipleFileChange, undefined, input?.onFocus)

  return (
    <FileView
      ref={ref}
      noDelete={fieldIndex !== undefined}
      onUploadMultiple={disabled || readOnly ? undefined : handleUploadMultiple}
      onFileUpload={disabled || readOnly ? undefined : onFileUpload}
      onChange={disabled || readOnly ? undefined : v => input.onChange(v)}
      field={field}
      data={input.value}
    />
  )
}
export const FileInput = forwardRef<InputRef, InputProps<FileField>>(PreFileInput)

export const EditableFileView = ({
  data,
  onSubmit,
  correction,
  field,
  baseStoragePath,
  fieldPathSegments,
  // alternateValue,
  stackProps,
  selectedIndex,
  onSelect,
  focusOnMount,
  isCorrecting,
  header,
}: {
  onSubmit?: (data: FileDBValue | null) => Promise<UpdateCallback>
  data?: FileDBValue
  correction?: FileDBValue
  isCorrecting?: boolean
  header?: ReactNode
  field: FileField
  selectedIndex?: number
  // alternateValue?: AlternateValue | null
  onSelect?: (checked: boolean) => void
  baseStoragePath: string
  fieldPathSegments: Array<string>
  stackProps?: StackProps
  focusOnMount?: boolean
}) => {
  const { auth } = useFirebase()
  const { admin } = useAuth()
  const { appName } = useApp()
  const [uploads, setUploads] = useState<Record<string, UploadProgress>>({})
  const [saving, setSaving] = useState(false)

  const handleChange = useCallback(
    async (updated: Array<ChangedFile>) => {
      if (!onSubmit) return { error: 'No submit function' }
      const uid = auth?.currentUser?.uid
      if (!uid) return { error: 'Not logged in' }
      if (updated.length) {
        try {
          setSaving(true)
          const processed = await processFileFieldData(
            appName,
            admin,
            uid,
            baseStoragePath,
            fieldPathSegments,
            field,
            updated[0].file,
            isCorrecting ? correction : data,
            u => {
              setUploads({ value: u })
            },
          )
          setUploads({})
          const res = await onSubmit(processed ?? null)
          setSaving(false)
          return res
        } catch (err) {
          console.error(err)
          setSaving(false)
          return { error: 'Error saving' }
        }
      } else {
        const res = await onSubmit(null)
        const currStoragePath = isCorrecting ? correction?.storagePath : data?.storagePath
        if (currStoragePath) {
          await deleteFile(currStoragePath)
        }
        return res
      }
    },
    [
      baseStoragePath,
      field,
      fieldPathSegments,
      onSubmit,
      data,
      auth,
      appName,
      admin,
      correction,
      isCorrecting,
    ],
  )

  const value = useMemo(
    () =>
      (correction && isCorrecting ? mergeData(data ?? {}, correction ?? {}) : data) as
        | FileDBValue
        | UnuploadedFileDBValue
        | undefined,
    [data, correction, isCorrecting],
  )

  const onFileUpload = useUploadDbFileValue(handleChange)
  const inputRef = useRef<InputRef>(null)
  useEffect(() => {
    if (focusOnMount && inputRef.current) {
      inputRef.current.focus()
    }
  }, [focusOnMount])

  return (
    <VStack width="100%" spacing={0} {...stackProps}>
      <Flex w="100%">
        <Box flex={1} minW={0}>
          <FileView
            data={value}
            ref={inputRef}
            onChange={onSubmit}
            isLoading={saving}
            header={header}
            field={field}
            onFileUpload={onFileUpload}>
            {onSelect ? (
              <Flex gap={2} align="center" pr={1}>
                {selectedIndex !== undefined && selectedIndex !== -1 ? (
                  <Center
                    w={5}
                    h={5}
                    bg="white"
                    border={`1px solid ${colors.green.hex}`}
                    fontSize="xs"
                    fontFamily="Hero-New"
                    fontWeight={700}
                    color={colors.green.hex}
                    borderRadius="50%">
                    <span>{selectedIndex + 1}</span>
                  </Center>
                ) : null}
                <ChakraCheckbox
                  colorScheme="green"
                  size="lg"
                  checked={selectedIndex !== undefined && selectedIndex !== -1}
                  onChange={e => onSelect(e.target.checked)}
                />
              </Flex>
            ) : null}
          </FileView>
        </Box>
        {/* {alternateValue ? <AlternateValuePopover {...alternateValue} /> : null} */}
      </Flex>
      <UploadProgressView uploads={uploads} />
    </VStack>
  )
}

export const AlternateInput: React.FC<InputProps<AlternateField>> = forwardRef<
  InputRef,
  InputProps<AlternateField>
>(({ field, input, disabled, readOnly }, ref) => {
  const { placeholder, fieldType, optional } = field
  const { value, onChange } = input
  useEffect(() => {
    if (typeof value !== 'object') onChange({ main: value })
  }, [value, onChange])

  return (
    <Grid width="100%" templateColumns="1fr 1fr" columnGap={3}>
      <Field name={`${input.name}.main`}>
        {props => (
          <Input
            disabled={disabled}
            readOnly={readOnly}
            ref={ref}
            field={{ type: fieldType, placeholder, optional } as IField}
            {...props}
          />
        )}
      </Field>
      <Field name={`${input.name}.alternate`}>
        {props => (
          <Input
            disabled={disabled}
            readOnly={readOnly}
            field={
              {
                type: fieldType,
                placeholder: `Alternate ${placeholder}`,
                optional: true,
              } as IField
            }
            {...props}
          />
        )}
      </Field>
    </Grid>
  )
})

const SignatureInput: IInputElement<SignatureField> = forwardRef<
  HTMLInputElement,
  InputProps<SignatureField>
>((props, ref) => {
  const { input, disabled, style, readOnly, field } = props
  return (
    <SignatureInputBody
      ref={ref}
      style={style}
      value={input.value}
      field={field}
      onBlur={input.onBlur}
      onFocus={input.onFocus}
      onChange={input.onChange}
      disabled={disabled}
      readOnly={readOnly}
    />
  )
})

export const InputElements: Record<FieldTypes, IInputElement<any>> = {
  [FieldTypes.TEXT]: TextInput,
  [FieldTypes.TEXTAREA]: TextArea,
  [FieldTypes.DROPDOWN]: DropdownInput,
  [FieldTypes.BOOLEAN]: BooleanDropdown,
  [FieldTypes.DATE]: DateInput,
  [FieldTypes.TIME]: TimeInput,
  [FieldTypes.DATETIME]: DateTimeInput,
  [FieldTypes.DOLLAR_AMOUNT]: DollarAmountInput,
  [FieldTypes.PERCENTAGE]: PercentageInput,
  [FieldTypes.ID]: DatabaseDropdown,
  [FieldTypes.PHONE]: PhoneInput,
  [FieldTypes.EMAIL]: EmailInput,
  [FieldTypes.DYNAMIC_DROPDOWN]: DynamicDropdown,
  [FieldTypes.CHECKBOX]: Checkbox,
  [FieldTypes.FILE]: FileInput,
  [FieldTypes.ALTERNATE]: AlternateInput,
  [FieldTypes.NUMBER]: NumberInput,
  [FieldTypes.HEIGHT]: HeightInput,
  [FieldTypes.MULTIPLE_SELECT]: MultipleSelectInput,
  [FieldTypes.SIGNATURE]: SignatureInput,
  [FieldTypes.INITIALS]: InitialsInput,
  [FieldTypes.EXAM_SECTION]: ExamSectionInput,
  [FieldTypes.BLOOD_PRESSURE]: BloodPressureInput,
}

const makeOnDragEndFunction = (fields: any) => (result: DropResult) => {
  // dropped outside the list
  if (!result.destination) {
    return
  }
  fields.move(result.source.index, result.destination.index)
}

const FormHeader = ({ children, id, ...props }: HeadingProps & { id: string }) => (
  <Header
    id={`stage-${id}`}
    fontWeight={500}
    opacity={1}
    py={1}
    textAlign="left"
    width="100%"
    fontSize="xl"
    {...props}>
    {children}
  </Header>
)

const useListItemLabel = (
  index: number,
  itemName: string,
  displayedFieldName: ListField['displayedField'],
) =>
  useMemo(() => {
    if (displayedFieldName === 'alpha') return `${itemName} ${ALPHABET[index]}`
    if (displayedFieldName === 'alpha-index') {
      return `${itemName} ${ALPHABET[index]} (${index + 1})`
    }
    return `${itemName} ${index + 1}`
  }, [displayedFieldName, index, itemName])

interface ListItemInputProps<FieldType = IField | FieldMap, Value = FieldMapValue | any> {
  field: FieldType
  active?: boolean
  index: number
  displayedField?: string
  width: string
  readOnly?: boolean
  disabled?: boolean
  itemName?: string
  value: Value
  onDelete: () => Promise<any>
  name: string
}
export const focusableSelector =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'

const ListItemMapInput = forwardRef<InputRef, ListItemInputProps<FieldMap, FieldMapValue>>(
  (
    {
      field,
      active,
      displayedField = 'name',
      index,
      value,
      itemName = 'Item',
      onDelete,
      name,
      width,
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState(
      isField(field) ? true : (field.initExpanded ?? Object.keys(value).length === 0),
    )
    const size = useInputSize()
    const tokens = getInputSizeTokens(size)
    const displayed = useListItemLabel(index, itemName, displayedField)

    const contentRef = useRef<HTMLDivElement>(null)
    useImperativeHandle(ref, () => ({
      // Focus the first child input of the field map
      focus: () => {
        setExpanded(true)
        requestAnimationFrame(() => {
          contentRef.current?.querySelector<HTMLElement>(focusableSelector)?.focus()
        })
      },
      blur: () => {
        const activeElement = document.activeElement
        if (activeElement instanceof HTMLElement && contentRef.current?.contains(activeElement)) {
          activeElement.blur()
        }
      },
    }))

    return (
      <Draggable key={name} index={index} draggableId={name}>
        {(provided, snapshot) => (
          <Box
            width={width}
            ref={provided.innerRef}
            overflow="hidden"
            bg="white"
            border="1px solid #cdcdcd"
            borderRadius="4px"
            boxShadow={snapshot.isDragging ? '0 0 4px rgba(0,0,0,0.25)' : '0 0 4px rgba(0,0,0,0.1)'}
            background={snapshot.isDragging ? 'white' : undefined}
            {...provided.draggableProps}>
            <Flex
              align="center"
              width="100%"
              bg="gray.50"
              borderTopRadius="4px"
              p={0}
              justify="space-between"
              // borderBottom='1px solid #eee'
            >
              <Flex align="center">
                <Flex px={2} {...provided.dragHandleProps}>
                  <DragHandleIcon color="gray.600" boxSize={tokens.fontSize} />
                </Flex>
                {/* <img
                {...provided.dragHandleProps}
                src={dragIcon}
                style={{ height: '85%', padding: '5px' }}
                alt='drag'
              /> */}
                <Text fontWeight={500} color="sage.600" fontSize={tokens.fontSize}>
                  {displayed === 'None' ? `${itemName} ${index + 1}` : displayed}
                </Text>
              </Flex>
              <ViewButton isOpen={expanded} onClick={() => setExpanded(!expanded)} />
              <HeaderButton label="Delete" size="medium" active>
                <DeleteButton itemName={itemName} onDelete={onDelete} />
              </HeaderButton>
            </Flex>
            <Collapse style={{ width: '100%' }} in={expanded}>
              <Flex ref={contentRef} w="100%" borderTop="1px solid #cdcdcd" py={1} px={2}>
                <FormElement name={name} listIndex={index} active={active} field={field} />
              </Flex>
            </Collapse>
          </Box>
        )}
      </Draggable>
    )
  },
)

const ListItemFieldInput = forwardRef<InputRef, ListItemInputProps<IField>>(
  (
    {
      field,
      active,
      displayedField = 'name',
      index,
      itemName = 'Item',
      onDelete,
      readOnly,
      disabled,
      width,
      name,
    },
    ref,
  ) => {
    const displayed = useListItemLabel(index, itemName, displayedField)

    const isFileField = field.type === FieldTypes.FILE
    return (
      <HStack bg="white" boxShadow="md" w={width} py={1} px={2} spacing={2} borderRadius={4}>
        <Box minW="0" p={isFileField ? 0 : 1} flex={1} borderRight="1px solid #cdcdcd">
          <FormElement
            name={name}
            readOnly={readOnly}
            disabled={disabled}
            ref={ref}
            listIndex={index}
            active={active}
            field={{ ...field, placeholder: displayed }}
          />
        </Box>
        <DeleteButton size="sm" noConfirm itemName={itemName} onDelete={onDelete} />
      </HStack>
    )
  },
)

const ListItemInput = forwardRef<InputRef, ListItemInputProps>(({ field, ...props }, ref) => {
  if (isField(field)) {
    return <ListItemFieldInput ref={ref} field={field} {...props} />
  }
  return <ListItemMapInput ref={ref} field={field} {...props} />
})

const ListInput: React.FC<ListInputProps> = props => {
  const { field, fields, active, name, readOnly, disabled } = props
  const { itemFields, itemName, horizontalConfig } = field
  const { value } = fields
  const size = useInputSize()
  const tokens = getInputSizeTokens(size)
  const listItemField = useMemo(() => {
    if (isField(itemFields)) return itemFields
    return { ...itemFields, initExpanded: true }
  }, [itemFields])

  const fieldRefs = useRef<Array<InputRef | null>>([])

  const itemWidth =
    typeof horizontalConfig?.itemWidth === 'number'
      ? `${horizontalConfig.itemWidth - 10}px`
      : '100%'
  return (
    <DragDropContext onDragEnd={makeOnDragEndFunction(fields)}>
      <Droppable droppableId={name}>
        {provided => (
          <Flex
            w="100%"
            ref={provided.innerRef}
            {...provided.droppableProps}
            flexFlow="column"
            style={{
              alignItems: 'flex-start',
            }}>
            <Flex
              align={horizontalConfig ? 'space-between' : 'flex-start'}
              w="100%"
              flexFlow={horizontalConfig ? 'row wrap' : 'column'}>
              {fields.length ? (
                fields.map((fieldName, index) => (
                  <ExpandOnMount
                    style={{
                      width: horizontalConfig?.itemWidth ?? '100%',
                      padding: '0.25rem 0.15rem',
                    }}
                    key={fieldName}>
                    <ListItemInput
                      ref={el => {
                        fieldRefs.current[index] = el
                      }}
                      displayedField={field.displayedField}
                      index={index}
                      name={fieldName}
                      readOnly={readOnly}
                      disabled={disabled}
                      itemName={itemName}
                      width={itemWidth}
                      value={value?.[index]}
                      active={active}
                      field={listItemField}
                      onDelete={async () => {
                        fields.remove(index)
                        return true
                      }}
                    />
                  </ExpandOnMount>
                ))
              ) : (
                <Text fontStyle="italic" color="gray.600" fontSize={tokens.placeholderFontSize}>
                  No {makePlural(itemName)} added
                </Text>
              )}
            </Flex>
            {provided.placeholder}
          </Flex>
        )}
      </Droppable>
      <RoundedButton
        mt={2}
        size={tokens.buttonSize}
        tabIndex={0}
        onClick={() => {
          const oldLength = fields.length ?? 0
          fields.push(isField(itemFields) ? '' : { createdOn: Date.now() })
          setTimeout(() => {
            fieldRefs.current[oldLength]?.focus()
          }, 10)
        }}>
        + Add {itemName}
      </RoundedButton>
    </DragDropContext>
  )
}

interface FormFieldMapProps extends FormElementProps {
  field: FieldMap
}

interface FormFieldProps {
  field: IField | ListField
  id?: string
  basePath?: string
  active?: boolean
  disabled?: boolean
  disabledReason?: string
  inputStyle?: CSSProperties
  showName?: boolean
  style?: CSSProperties
  readOnly?: boolean
  showErrorIfClean?: boolean
  inEditable?: boolean
}

interface FormDefaultFieldProps extends FormFieldProps {
  field: IField
}

interface FormListFieldProps extends FormFieldProps {
  field: ListField
}

const FormListField: React.FC<FormListFieldProps> = ({
  field,
  id,
  basePath,
  active,
  readOnly,
  showErrorIfClean,
}) => {
  const { labels, hints, name } = field

  const populatedLabels = usePopulatedLabels(labels)
  const populatedHints = usePopulatedHints(hints)
  const { theme, hideHints, hideLabels } = useContext(ThemeContext)
  const size = useInputSize()
  const tokens = getInputSizeTokens(size)
  return (
    <Box px={2} width="100%">
      <FieldArray name={`${basePath}${id}`}>
        {props => {
          const arrayError = props.meta.error || props.meta.submitError
          return (
            <Container
              style={{
                height: 'auto',
                marginBottom: theme === 'detailed' ? 2 : 0,
                alignItems: 'flex-start',
              }}>
              {/* {placeholderAbove ? ( */}
              <Text fontSize={tokens.placeholderFontSize} color="gray.600" fontWeight={600}>
                {name}
              </Text>
              {/* ) : null} */}
              {!hideLabels ? <Labels labels={populatedLabels} /> : null}
              {!hideHints ? <Hints hints={populatedHints} /> : null}
              <ListInput
                readOnly={readOnly}
                basePath={basePath ?? ''}
                name={id ?? ''}
                active={active}
                field={field}
                showErrorIfClean={showErrorIfClean}
                {...props}
              />
              <CollapseError
                error={
                  props.meta.touched && typeof arrayError === 'string' ? arrayError : undefined
                }
                style={{ borderRadius: '6px' }}
              />
            </Container>
          )
        }}
      </FieldArray>
    </Box>
  )
}

const FormFieldAlternateValueView = ({
  name,
  shared,
  field,
  children,
}: PropsWithChildren<{
  name: string
  shared: ShareCorrections
  field: IField
}>) => {
  const corrections = useCorrections()
  const value = useMemo(() => nestedGet(corrections, name), [corrections, name])
  const { value: formatted } = useFormattedValue(field, value)
  if (value === null || value === undefined) return <>{children}</>
  return (
    <Flex align="center" w="100%">
      <Box flex={1} minW="0">
        {children}
      </Box>
      <AlternateValuePopover group={shared.group} value={formatted} />
    </Flex>
  )
}

const FormDefaultField = forwardRef<InputRef, FormDefaultFieldProps>(
  (
    {
      field,
      id,
      basePath,
      inputStyle,
      style,
      disabled,
      readOnly,
      showErrorIfClean,
      inEditable,
      showName,
      disabledReason,
    },
    ref,
  ) => {
    const correctionsShared = useShareCorrections()
    const collectionItems = useCollections()
    const formatFieldValue: FieldFormatter = field.format ?? fieldFormat[field.type] ?? (v => v)
    const parseValue = field.parse ?? fieldParse[field.type] ?? (v => v)
    const format =
      field.format || fieldFormat[field.type]
        ? (val?: string) => formatFieldValue(val, field, collectionItems)
        : undefined
    const parse =
      field.parse || fieldParse[field.type] ? (val?: string) => parseValue(val) : undefined

    const body = (
      <Field
        format={format}
        parse={parse}
        // validate={validate}
        name={`${basePath}${id}`}
        render={({ input, meta }) => {
          const C = InputElements[field.type]
          if (!C) {
            console.error(`Cant find component for field type: ${field.type}`)
            return undefined
          }
          return (
            <>
              <Input
                ref={ref}
                style={{ opacity: disabled ? 0.8 : 1, ...style }}
                inputStyle={inputStyle}
                meta={meta}
                showName={showName}
                disabled={disabled}
                readOnly={readOnly}
                disabledReason={disabledReason}
                field={field}
                input={input}
                showErrorIfClean={showErrorIfClean}
                inEditable={inEditable}
              />
            </>
          )
        }}
      />
    )
    return correctionsShared.share ? (
      <FormFieldAlternateValueView name={id ?? ''} field={field} shared={correctionsShared.share}>
        {body}
      </FormFieldAlternateValueView>
    ) : (
      body
    )
  },
)

const FormField = forwardRef<InputRef, FormFieldProps>(({ field, id = '', ...props }, ref) => {
  const body = isListField(field) ? (
    <FormListField id={id} {...props} field={field} />
  ) : (
    <FormDefaultField ref={ref} id={id} {...props} field={field} />
  )

  return body
})

const FlexFieldContainerBody = ({
  values,
  basePath,
  field,
  condition,
  children,
}: PropsWithChildren<{
  basePath: string
  values: FieldMapValue | undefined
  condition: FieldCondition
  field: IField
}>) => {
  const flexValue = field.row?.flex ?? 1
  const isIn = useMemo(() => {
    let bp = basePath
    if (bp.length) {
      bp = bp
        .substring(0, bp.length - 1)
        .replace(/\[/g, '.')
        .replace(/\]/g, '')
    }
    return condition(bp && values ? nestedGet(values, bp) : values)
  }, [basePath, values, condition])
  if (!isIn) return null
  return (
    <Box flex={flexValue} minW={0}>
      {children}
    </Box>
  )
}

const FlexFieldContainer = ({
  children,
  field,
  basePath,
}: PropsWithChildren<{ field: IField; basePath: string }>) => {
  const { condition } = field
  return condition ? (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => (
        <FlexFieldContainerBody
          values={values}
          basePath={basePath}
          field={field}
          condition={condition}>
          {children}
        </FlexFieldContainerBody>
      )}
    </FormSpy>
  ) : (
    <Box flex={field.row?.flex ?? 1} minW={0}>
      {children}
    </Box>
  )
}

const useFieldGroups = (
  children: FieldMap['children'],
): Array<
  | {
      type: 'row'
      rowIndex: number
      fields: Array<{ fieldId: string; field: IField }>
    }
  | {
      type: 'ungrouped'
      fieldId: string
      field: IField | ListField | FieldMap | InfoStage
    }
> => {
  const { mobileLayout } = useFormWidth()

  return useMemo(() => {
    const fieldIds = Object.keys(children)

    // If mobile, return all fields as ungrouped (ignore row property)
    if (mobileLayout) {
      return fieldIds.map(fieldId => ({
        type: 'ungrouped' as const,
        fieldId,
        field: children[fieldId],
      }))
    }

    // Process fields in order, grouping consecutive fields with the same row.index
    const groups: Array<
      | {
          type: 'row'
          rowIndex: number
          fields: Array<{ fieldId: string; field: IField }>
        }
      | {
          type: 'ungrouped'
          fieldId: string
          field: IField | ListField | FieldMap | InfoStage
        }
    > = []

    let currentRowGroup: {
      rowIndex: number
      fields: Array<{ fieldId: string; field: IField }>
    } | null = null

    // Track row indices that have been completed (closed after encountering ungrouped field)
    const completedRowIndices = new Set<number>()

    fieldIds.forEach(fieldId => {
      const childField = children[fieldId]

      // Check if field has row prop (only IField can have row)
      if (isField(childField) && childField.row) {
        const rowIndex = childField.row.index

        // Check if this row index was already completed - if so, error!
        if (completedRowIndices.has(rowIndex)) {
          throw new Error(
            `Fields with row.index ${rowIndex} are not consecutive. Field "${fieldId}" has row.index ${rowIndex} but fields with this row.index were already closed.`,
          )
        }

        if (currentRowGroup) {
          if (currentRowGroup.rowIndex === rowIndex) {
            // Same row index, add to current group
            currentRowGroup.fields.push({ fieldId, field: childField })
          } else {
            // Different row index, close current group and start new one
            groups.push({
              type: 'row',
              rowIndex: currentRowGroup.rowIndex,
              fields: currentRowGroup.fields,
            })
            currentRowGroup = {
              rowIndex,
              fields: [{ fieldId, field: childField }],
            }
          }
        } else {
          // Start a new row group
          currentRowGroup = {
            rowIndex,
            fields: [{ fieldId, field: childField }],
          }
        }
      } else {
        // Ungrouped field - close current row group if any
        if (currentRowGroup) {
          groups.push({
            type: 'row',
            rowIndex: currentRowGroup.rowIndex,
            fields: currentRowGroup.fields,
          })
          // Mark this row index as completed
          completedRowIndices.add(currentRowGroup.rowIndex)
          currentRowGroup = null
        }
        groups.push({ type: 'ungrouped', fieldId, field: childField })
      }
    })

    // Close any remaining row group
    if (currentRowGroup) {
      const { rowIndex, fields } = currentRowGroup
      groups.push({
        type: 'row',
        rowIndex,
        fields,
      })
    }

    return groups
  }, [children, mobileLayout])
}

const FormFieldMap: React.FC<FormFieldMapProps> = ({
  active,
  name: id = '',
  field,
  listIndex: index,
  basePath = '',
  depth,
  readOnly,
  disabled,
  root,
  style,
  inputStyle,
  showName = true,
  showErrorIfClean,
}) => {
  const { theme, hideHints, hideLabels } = useContext(ThemeContext)
  const size = useInputSize()
  const tokens = getInputSizeTokens(size)
  const { labels, hints } = field
  const populatedLabels = usePopulatedLabels(labels)
  const populatedHints = usePopulatedHints(hints)

  // Process fields in order, grouping consecutive fields with the same row.index
  const fieldGroups = useFieldGroups(field.children)

  const childBasePath = `${basePath ? basePath : ''}${id ? `${id}.` : ''}`

  const renderField = (
    fieldId: string,
    childField: IField | ListField | FieldMap | InfoStage,
    prevField: IFormElement | undefined,
    index: number,
  ) => {
    let bottomSpacer = null
    // const isRadio = childField.type === FieldTypes.DROPDOWN && childField.inputType === 'radio'

    if (
      isField(childField) &&
      childField.type !== FieldTypes.CHECKBOX &&
      index !== fieldGroups.length - 1
    )
      bottomSpacer = tokens.bottomSpacer
    return (
      <FormElement
        disabled={disabled}
        key={fieldId}
        readOnly={readOnly}
        active={active}
        depth={depth !== undefined ? depth + 1 : undefined}
        showErrorIfClean={showErrorIfClean}
        inputStyle={inputStyle}
        prevField={prevField}
        field={childField}
        basePath={childBasePath}
        name={fieldId}
        mapIndex={index}
        bottomSpacer={bottomSpacer}
      />
    )
  }

  // Section-header font is size-aware (a touch larger than the field placeholder) and still
  // scales by nesting: root section > depth-1 map > deeper maps.
  let headerFontSize = tokens.fieldMapFontSize.sub
  if (root) headerFontSize = tokens.fieldMapFontSize.root
  else if (depth === 1) headerFontSize = tokens.fieldMapFontSize.nested

  return (
    <Container alignItems="flex-start" width="100%" transition="opacity 500ms" style={style}>
      {showName && field.name && index === undefined && theme !== 'basic' ? (
        <FormHeader
          _dark={{ color: root ? 'gray.100' : 'gray.300' }}
          color={root ? 'gray.800' : 'gray.600'}
          fontSize={headerFontSize}
          id={`stage-${id}`}>
          {/* color="green.500" */}
          {field.name}
        </FormHeader>
      ) : null}
      {!hideLabels && labels ? <Labels labels={populatedLabels} /> : null}
      {!hideHints && hints ? <Hints hints={populatedHints} /> : null}

      <VStack px={1} spacing={0} w="100%" align="flex-start" pb={1}>
        {fieldGroups.map((group, groupIndex) => {
          const prevField = groupIndex > 0 ? fieldGroups[groupIndex - 1] : undefined
          if (group.type === 'row') {
            return (
              <Flex
                key={`row-${group.rowIndex}-${groupIndex}`}
                w="100%"
                gap={tokens.rowGap}
                alignItems="flex-end">
                {group.fields.map(({ fieldId, field: childField }) => (
                  <FlexFieldContainer key={fieldId} basePath={childBasePath} field={childField}>
                    {renderField(
                      fieldId,
                      childField,
                      prevField?.type === 'ungrouped' ? prevField.field : undefined,
                      groupIndex,
                    )}
                  </FlexFieldContainer>
                ))}
              </Flex>
            )
          } else {
            return renderField(
              group.fieldId,
              group.field,
              prevField?.type === 'ungrouped' ? prevField.field : undefined,
              groupIndex,
            )
          }
        })}
      </VStack>
    </Container>
  )
}

export const InfoStageView = ({ field }: { field: InfoStage }) => (
  <DBTextStage
    style={field.style}
    version={field.editorVersion || 'v1'}
    content={{ text: field.data }}
  />
)

export const FormElement = forwardRef<InputRef, FormElementProps>((props, ref) => {
  const {
    field,
    name,
    basePath = '',
    active,
    disabled,
    listIndex: index,
    root,
    inputStyle,
    mapIndex,
    prevField,
    depth: depthProp,
    style,
    readOnly,
    showErrorIfClean,
    disabledReason,
    inEditable,
    bottomSpacer,
    size,
    showName = true,
  } = props
  const depth = root ? 0 : depthProp
  const themeCtx = useContext(ThemeContext)
  const { hideInfo } = themeCtx
  let C: React.ReactElement = <></>
  if (isInfoStage(field)) {
    if (!hideInfo) {
      C = <InfoStageView field={field} />
    }
  } else if (isListField(field) || isField(field)) {
    C = (
      <FormField
        ref={ref}
        disabled={disabled}
        disabledReason={disabledReason}
        active={active}
        readOnly={readOnly}
        style={style}
        inputStyle={inputStyle}
        showErrorIfClean={showErrorIfClean}
        field={field}
        basePath={basePath}
        inEditable={inEditable}
        showName={showName}
        id={name}
      />
    )
  } else {
    C = (
      <FormFieldMap
        root={root}
        listIndex={index}
        readOnly={readOnly}
        style={style}
        inputStyle={inputStyle}
        showErrorIfClean={showErrorIfClean}
        disabled={disabled}
        {...props}
        depth={root ? 0 : depth}
        field={field as FieldMap}
      />
    )
  }

  let body = (
    <>
      {(!isField(field) || (prevField && !isField(prevField) && !isInfoStage(prevField))) &&
      mapIndex !== undefined &&
      mapIndex > 0 &&
      depth !== undefined &&
      depth < 2 ? (
        <Divider borderColor="gray.400" my={2} />
      ) : null}
      {C}
      {bottomSpacer ? <Box h={bottomSpacer} /> : null}
    </>
  )

  // A `size` set on this FormElement propagates to every descendant input via context — inputs
  // resolve their size through `useInputSize()`, so no prop-drilling is needed. Only wrap when a
  // size is explicitly provided (typically once, at the form root) to avoid nested providers.
  if (size && size !== themeCtx.inputSize) {
    body = (
      <ThemeContext.Provider value={{ ...themeCtx, inputSize: size }}>{body}</ThemeContext.Provider>
    )
  }

  const { condition } = field ?? {}
  return condition ? (
    <Condition condition={condition} basePath={basePath}>
      {body}
    </Condition>
  ) : (
    body
  )
})

// const inputContainerStyle: CSSProperties = {
//   height: 'auto',
//   width: '100%',
//   alignItems: 'flex-start',
//   paddingBottom: '0.2rem',

// }
const Input = forwardRef<InputRef, InputProps<IField>>(
  (
    {
      field,
      input,
      meta,
      style,
      disabled,
      readOnly,
      showErrorIfClean,
      inputStyle,
      inEditable,
      disabledReason,
      size: sizeProp,
      showName = true,
    },
    ref,
  ) => {
    const { type, labels, hints, placeholder, presets, row } = field
    const { active } = meta
    const size = useInputSize(sizeProp ?? field?.size)
    const isAlternateField = field.type === FieldTypes.ALTERNATE
    const isCheckboxField = field.type === FieldTypes.CHECKBOX
    const populatedLabels = usePopulatedLabels(!isAlternateField ? labels : undefined)
    const populatedHints = usePopulatedHints(!isAlternateField ? hints : undefined)
    const InputElement = useMemo(() => {
      if (InputElements[type]) {
        return InputElements[type]
      }
      return undefined
    }, [type])

    const {
      theme,
      hideLabels,
      hideHints,
      placeholderAbove,
      tooltipError,
      tooltipErrorPlacement = 'top',
      errorTooltipProps,
    } = useContext<ThemeContextData<TooltipProps>>(ThemeContext)

    const shouldUseTooltipError = tooltipError || !!row

    const hintLabel = useMemo(
      () => populatedHints?.map(({ text }) => text).join(' | '),
      [populatedHints],
    )

    const shouldShowError = useMemo(() => {
      if (active) return false
      if (showErrorIfClean) return !!(meta.submitError || meta.error)
      return meta.touched && !!(meta.submitError || meta.error)
    }, [meta.touched, meta.submitError, meta.error, showErrorIfClean, active])

    const shouldShowWarning = useMemo(() => {
      if (!field.warningMessage) return false
      if (active) return false
      if (shouldShowError) return false
      return isFieldValueEmpty(field, input.value)
    }, [field, input.value, active, shouldShowError])

    const { errorColor, errorBg } = useMemo(() => {
      if (showErrorIfClean && !meta.touched) return { errorColor: 'gray.700', errorBg: 'gray.100' }
      return { errorColor: 'red.800', errorBg: 'red.200' }
    }, [showErrorIfClean, meta.touched])

    const fullWidth = useMemo(() => {
      if (field.row) return '100%'
      if (inEditable) return '100%'
      switch (field.type) {
        case FieldTypes.NUMBER:
        case FieldTypes.HEIGHT:
        case FieldTypes.BLOOD_PRESSURE:
        case FieldTypes.BOOLEAN:
        case FieldTypes.DATE:
        case FieldTypes.MULTIPLE_SELECT:
          return 'auto'
        // case FieldTypes.DATE:
        // return '120px'
        case FieldTypes.INITIALS:
          return INITIALS_CONTAINER_WIDTH[size]
        default:
          return '100%'
      }
    }, [field, inEditable, size])

    // TODO: make error a badge
    const body = useMemo(
      () =>
        InputElement ? (
          <InputElement
            // basic={basic}
            style={{ width: fullWidth, ...style }}
            inputStyle={inputStyle}
            disabled={disabled}
            readOnly={readOnly}
            ref={ref}
            meta={meta}
            field={field}
            input={input}
            size={size}
            inEditable={inEditable}
          />
        ) : null,
      [
        InputElement,
        disabled,
        readOnly,
        ref,
        meta,
        field,
        input,
        inputStyle,
        inEditable,
        style,
        fullWidth,
        size,
      ],
    )

    const getFullBody = () => {
      if (disabled && disabledReason) {
        return (
          <Tooltip placement="top-start" bg="#808080" label={disabledReason}>
            <Box width={fullWidth}>{body}</Box>
          </Tooltip>
        )
      }

      if (shouldUseTooltipError && !disabled)
        return (
          <Tooltip
            placement={field.type === FieldTypes.SIGNATURE ? 'bottom' : tooltipErrorPlacement}
            hasArrow
            bg={errorBg}
            maxW="100%"
            color={errorColor}
            fontSize="md"
            label={shouldUseTooltipError ? meta?.error : undefined}
            isOpen={shouldShowError}
            {...errorTooltipProps}>
            <Box width={fullWidth}>{body}</Box>
          </Tooltip>
        )
      if (hideHints && hintLabel)
        return (
          <Tooltip
            placement="top-start"
            bg="#808080"
            isOpen={!!hintLabel && meta.active}
            label={hintLabel}>
            <Box width={fullWidth}>{body}</Box>
          </Tooltip>
        )
      return body
    }
    const fullBody = getFullBody()

    const shouldRenderPlaceholder = useMemo(() => {
      if (isCheckboxField || isAlternateField || !showName) return false
      if (type === FieldTypes.EXAM_SECTION) return false
      if (placeholderAbove === false) return false
      if (theme !== 'basic' || placeholderAbove) return true
      return true
    }, [type, placeholderAbove, theme, isCheckboxField, isAlternateField, showName])

    return (
      <>
        {!hideLabels && labels ? <Labels labels={populatedLabels} /> : null}
        {
          // !labels &&
          shouldRenderPlaceholder ? (
            <Text
              pb={hideHints || !hints?.length ? 0.5 : 0}
              fontSize={getInputSizeTokens(size).labelFontSize}
              color="gray.600"
              fontWeight={400}
              whiteSpace="pre-wrap">
              {placeholder}
            </Text>
          ) : null
        }
        {!hideHints && hints ? <Hints hints={populatedHints} /> : null}
        {presets ? <Presets field={field} onClickPreset={p => input.onChange(p)} /> : null}
        {fullBody}
        {!shouldUseTooltipError ? (
          <CollapseError
            inverted
            style={{ paddingBottom: 0 }}
            error={shouldShowError ? meta.submitError || meta.error : undefined}
          />
        ) : null}
        {field.warningMessage ? (
          <CollapseWarning warning={shouldShowWarning ? field.warningMessage : undefined} />
        ) : null}
      </>
    )
  },
)

const EditableResetter = ({
  isEditing,
  value,
  field,
}: {
  isEditing: boolean
  value: any
  field: IFormElement
}) => {
  const { initialize, focus } = useForm()
  useEffect(() => {
    if (isField(field)) {
      if (!isEditing) {
        if (isField(field)) {
          initialize({ value })
        } else initialize(value)
      }
    }
  }, [isEditing, value, initialize, field, focus])

  return null
}

interface EditableBodyProps {
  id: string
  field: IFormElement
  onSubmit: FormRenderProps['handleSubmit']
  onClose: () => void
  stackProps?: StackProps
  inputStyle?: CSSProperties
  formElementStyle?: CSSProperties
}

const editableBodyFormElementStyle: CSSProperties = {
  padding: '0.1rem 0.3rem',
}

const EditableBody = forwardRef<InputRef, EditableBodyProps>(
  ({ id, field, onSubmit, onClose, stackProps, inputStyle, formElementStyle }, ref) => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const handleSubmit = useCallback(
      async (data: FieldMapValue) => {
        setIsSubmitting(true)
        const res = await onSubmit(data)
        setIsSubmitting(false)
        return res
      },
      [onSubmit],
    )

    // const inputRef = useRef<InputRef>(null)
    // useEffect(() => {
    //   if (!ref) return
    //   const asInputRef = ref as RefObject<InputRef>
    //   asInputRef.current?.focus()
    // }, [ref])

    const styleProp = useMemo<CSSProperties>(
      () => ({
        paddingBottom: 0,
        ...inputStyle,
      }),
      [inputStyle],
    )

    return (
      <HStack
        flex={1}
        zIndex={2}
        pr={1}
        // pt={1}
        borderRadius={4}
        boxShadow="md"
        minW="220px"
        bg="white"
        _dark={{ bg: 'gray.800' }}
        w="100%"
        align="center"
        onClick={e => e.stopPropagation()}
        {...stackProps}>
        <Center w="100%" minW="0" flex={1}>
          <FormElement
            style={{ ...editableBodyFormElementStyle, ...formElementStyle }}
            ref={ref}
            active
            inEditable
            name={id}
            field={field}
            inputStyle={styleProp}
          />
        </Center>
        <Stack
          direction={field.type === FieldTypes.TEXTAREA ? 'column' : 'row'}
          py={1}
          minW="auto"
          align="center"
          justify="center"
          spacing={1}>
          <IconButton
            aria-label="Save"
            icon={<CheckIcon />}
            w="22px"
            h="22px"
            minH="0"
            color="white"
            size="xs"
            bg="green.400"
            isLoading={isSubmitting}
            onClick={handleSubmit}
          />
          <IconButton
            aria-label="Cancel"
            color="white"
            w="22px"
            h="22px"
            minH="0"
            icon={<CloseIcon />}
            size="xs"
            bg="gray.400"
            onClick={onClose}
          />
        </Stack>
      </HStack>
    )
  },
)

export const EditablePopover = ({
  id,
  field,
  handleSubmit,
}: PropsWithChildren<{
  id: string
  field: IFormElement
  handleSubmit: FormRenderProps['handleSubmit']
}>) => {
  const { openedOn: popoverId, registerPopover, onClose } = useContext(EditableStateContext)
  const [openedOn, setOpenedOn] = useState<number | null>(null)
  const form = useForm()
  const onSubmit = useCallback(async () => {
    const res = await handleSubmit()
    if (!res) onClose()
    return res
  }, [handleSubmit, onClose])
  return (
    <Popover
      strategy="fixed"
      onOpen={() => {
        const now = Date.now()
        setOpenedOn(now)
        registerPopover(now)
      }}
      closeOnBlur={false}
      isOpen={!!popoverId && popoverId === openedOn}
      onClose={() => {
        onClose()
        form.restart()
      }}>
      <PopoverTrigger>
        <IconButton
          aria-label="edit"
          icon={<EditIcon />}
          size="xs"
          color="gray.600"
          variant="ghost"
        />
      </PopoverTrigger>
      <PopoverContent w="auto">
        <PopoverArrow />
        <PopoverBody p={1}>
          <EditableBody onClose={onClose} id={id} field={field} onSubmit={onSubmit} />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

interface StandaloneInputProps<T extends IField = IField> {
  field: T
  value?: any
  onChange: (v?: any) => void
  onBlur?: () => void
  onFocus?: () => void
  theme?: 'basic' | 'detailed'
  disabled?: boolean
  style?: CSSProperties
  inputStyle?: CSSProperties
  size?: InputSize
  error?: string
}
const BaseStandaloneInput = <F extends IField>(
  {
    onChange,
    value,
    field,
    onBlur,
    onFocus,
    error,
    style,
    theme,
    disabled,
    inputStyle,
    size,
  }: StandaloneInputProps<F>,
  ref: ForwardedRef<InputRef>,
) => {
  const [isActive, setActive] = useState(false)
  const { value: formattedValue } = useFormattedValue(field, value)
  const parse = field.parse ?? fieldParse[field.type]
  const val = useMemo(() => {
    if (!value) return value
    switch (field.type) {
      case FieldTypes.CHECKBOX:
      case FieldTypes.DROPDOWN:
      case FieldTypes.ID:
      case FieldTypes.EXAM_SECTION:
      case FieldTypes.BLOOD_PRESSURE:
        return value
      default:
        return formattedValue
    }
  }, [field, formattedValue, value])
  return (
    <ThemeContext.Provider
      value={{ theme: theme || 'basic', placeholderAbove: false, inputSize: size }}>
      <Input
        ref={ref}
        field={field}
        style={style}
        inputStyle={inputStyle}
        disabled={disabled}
        input={{
          name: '',
          onChange: e => {
            if (e?.target) onChange(parse ? parse(e.target?.value) : e.target?.value)
            else onChange(parse ? parse(e) : e)
          },
          value: val,
          onBlur: () => {
            setActive(false)
            if (onBlur) onBlur()
          },
          onFocus: () => {
            setActive(true)
            if (onFocus) onFocus()
          },
        }}
        meta={{ error, active: isActive }}
      />
    </ThemeContext.Provider>
  )
}

export const StandaloneInput = forwardRef<InputRef, StandaloneInputProps>(BaseStandaloneInput)

export const Editable: React.FC<EditableProps> = ({
  onSubmit,
  onDelete,
  field,
  value,
  inGrid,
  correction,
  size,
  dataCellProps,
  dataCellTooltip,
  theme,
  id = 'value',
  previewIsButton,
  parentValue,
  parentCorrection: parentCorrections,
  onEditHovered,
  index,
  fieldPathSegments,
  baseStoragePath,
  style,
  adminView: adminViewProp,
  initEditing,
  editableStackProps,
  openCallback,
  formElementStyle,
  inputStyle,
  closeCallback,
}) => {
  const { adminView: adminViewContext } = useContext(DataViewContext)
  const adminView = adminViewProp ?? adminViewContext
  const shared = useShareCorrections()

  const [openedOn, setOpenedOn] = useState<number | null>(null)
  const { openedOn: popoverId, registerPopover, onClose } = useContext(EditableStateContext)
  const handleClose = useCallback(() => {
    onClose()
    if (closeCallback) closeCallback()
  }, [closeCallback, onClose])

  const inputRef = useRef<InputRef>(null)

  const onOpen = useCallback(() => {
    const now = Date.now()
    setOpenedOn(now)
    registerPopover(now)
    if (onEditHovered) onEditHovered(false)
    if (openCallback) openCallback()
  }, [registerPopover, openCallback, onEditHovered])

  useEffect(() => {
    if (openedOn && inputRef.current) inputRef.current.focus()
  }, [openedOn])

  const initEditingRef = useRef(initEditing)
  const onOpenRef = useRef(onOpen)
  useEffect(() => {
    if (initEditingRef.current && onOpenRef.current) onOpenRef.current()
  }, [])

  const { hasCorrection } = useMemo(() => {
    const hasV = value !== undefined && value !== null
    const hasC = correction !== undefined && correction !== null && correction !== value
    return { hasValue: hasV, hasCorrection: hasC }
  }, [value, correction])
  const {
    value: formattedValue,
    collection: collectionData,
    itemId: valueItemId,
  } = useFormattedValue(field, value, parentValue)
  const { value: formattedCorrection, itemId: correctionItemId } = useFormattedValue(
    field,
    correction,
    {
      ...parentValue,
      ...parentCorrections,
    },
  )
  const displayingCorrection = useMemo(() => adminView && hasCorrection, [hasCorrection, adminView])
  const alternateValue = useMemo<AlternateValue | null>(() => {
    if (!adminView)
      return shared && hasCorrection
        ? { group: 'admin', value: formattedCorrection as string }
        : null
    return hasCorrection ? { group: 'patient', value: formattedValue as string } : null
  }, [adminView, formattedCorrection, formattedValue, shared, hasCorrection])
  const displayed = useMemo(
    () => (displayingCorrection ? correction : value),
    [correction, value, displayingCorrection],
  )

  const item = useMemo(
    () =>
      getSelectedItem(collectionData.items, displayingCorrection ? correctionItemId : valueItemId),
    [displayingCorrection, collectionData, correctionItemId, valueItemId],
  )

  const isEditing = useMemo(() => !!(popoverId && popoverId === openedOn), [popoverId, openedOn])

  const submit = useCallback(
    async (data: FieldMapValue): Promise<ValidationErrors> => {
      const v = data[id]
      const getSubmittedValue = () => {
        switch (field.type) {
          case FieldTypes.CHECKBOX:
          case FieldTypes.BOOLEAN:
            return typeof v === 'boolean' ? v : null
          case FieldTypes.DOLLAR_AMOUNT:
          case FieldTypes.NUMBER:
          case FieldTypes.PERCENTAGE:
            return typeof v === 'number' ? v : null
          default:
            return v || null
        }
      }
      if (onSubmit) {
        try {
          const submittedValue = getSubmittedValue()
          const { success, error } = await onSubmit(submittedValue)
          if (success) {
            handleClose()
            return undefined
          }
          if (error) {
            return { [id]: error }
          }
          return undefined
        } catch (err) {
          console.error(err)
          return { [id]: 'An error occurred' }
        }
      } else {
        return {
          [id]: 'An error occurred',
        }
      }
    },
    [handleClose, id, onSubmit, field],
  )

  const redFlagReason = useMemo(() => {
    if (!adminView) return undefined
    if (field.type === FieldTypes.ID) {
      return item?.redFlaggedReason ? item?.redFlaggedReason || 'Red flagged' : undefined
    }
    return getFieldIsRedFlagged(field, displayed)
  }, [field, adminView, item, displayed])

  const validate = useCallback(
    (v: FieldMapValue) => {
      const parsed = v[id]
      if (!parsed && field.optional) return undefined
      const validateFunc = field.validate ?? defaultValidation[field.type]
      const err = validateFunc(parsed, field)
      if (err) {
        return { [id]: err }
      }
      return undefined
    },
    [field, id],
  )

  const displayedText = useMemo(
    () => (displayingCorrection ? formattedCorrection : formattedValue),
    [displayingCorrection, formattedCorrection, formattedValue],
  )

  if (field.type === FieldTypes.FILE) {
    if (!baseStoragePath) throw new Error('baseStoragePath is required for file fields')

    return (
      <EditableFileView
        baseStoragePath={baseStoragePath}
        data={displayingCorrection ? correction : value}
        // alternateValue={alternateValue}
        field={field}
        fieldPathSegments={
          fieldPathSegments && Array.isArray(fieldPathSegments) ? fieldPathSegments : []
        }
        onSubmit={
          onSubmit
            ? async data => {
                const res = await onSubmit(data)
                if (res) handleClose()
                return res
              }
            : undefined
        }
      />
    )
  }

  return field.type === FieldTypes.CHECKBOX ? (
    <Flex gap={2}>
      <EditableCheckbox
        value={displayed}
        alternateValue={alternateValue}
        index={index}
        inGrid={inGrid}
        field={field}
        style={style}
        id={id}
        onSubmit={onSubmit}
      />
    </Flex>
  ) : (
    <ThemeContext.Provider
      value={{
        hideHints: true,
        hideLabels: true,
        placeholderAbove: false,
        tooltipError: true,
        theme: theme || 'basic',
        inputSize: size,
      }}>
      <Flex
        // flexGrow={1}
        align={field.type === FieldTypes.TEXTAREA ? 'flex-start' : 'center'}
        style={style}
        width={field.type === FieldTypes.TEXTAREA ? '100%' : 'auto'}
        sx={
          index !== undefined
            ? {
                [`:nth-of-type(${inGrid ? '4n - 2' : '2n - 1'})`]: {
                  background: 'rgb(245,245,245)',
                },
              }
            : undefined
        }
        gap={0}
        py={field.type === FieldTypes.TEXTAREA ? 1 : 0}>
        {isEditing ? (
          <Form
            validate={validate}
            mutators={{ ...arrayMutators }}
            initialValues={{
              ...parentValue,
              [id]: displayingCorrection ? correction : value,
            }}
            onSubmit={submit}>
            {({ handleSubmit }) => (
              <>
                <EditableResetter field={field} isEditing={isEditing} value={value} />
                <EditableBody
                  stackProps={editableStackProps}
                  onSubmit={handleSubmit}
                  field={field}
                  ref={inputRef}
                  id={id}
                  inputStyle={inputStyle}
                  formElementStyle={formElementStyle}
                  onClose={handleClose}
                />
              </>
            )}
          </Form>
        ) : (
          <DataCell
            opacity={!displayedText || displayedText === 'None' ? 0.7 : 1}
            py={1}
            px={1}
            as={previewIsButton ? 'button' : 'div'}
            onClick={previewIsButton && onSubmit ? onOpen : undefined}
            flex={1}
            minW={0}
            fontSize={size ? getInputSizeTokens(size).fontSize : undefined}
            withTooltip={dataCellTooltip ? displayedText : null}
            // fontWeight={500}
            // whiteSpace={field.type === FieldTypes.TEXTAREA ? 'pre-wrap' : 'nowrap'}
            whiteSpace="pre-wrap"
            overflow="hidden"
            textOverflow="ellipsis"
            color={
              !field.optional && correction === undefined && value === undefined ? 'red' : undefined
            }
            {...dataCellProps}>
            {!onSubmit || (displayedText && displayedText !== 'None')
              ? displayedText
              : field.placeholder}
            {alternateValue ? <AlternateValuePopover {...alternateValue} /> : null}
            {redFlagReason ? (
              <ProfileRedFlagPopover
                boxProps={{ bg: 'transparent' }}
                redFlags={{
                  flag: {
                    header: field.placeholder,
                    reason: redFlagReason,
                  },
                }}
                ml={2}
              />
            ) : null}
          </DataCell>
        )}
        {previewIsButton || isEditing || !onSubmit ? null : (
          <HStack pl={2} pr={1} spacing={0}>
            <IconButton
              aria-label="Edit"
              onClick={e => {
                e.stopPropagation()
                onOpen()
              }}
              onPointerEnter={() => onEditHovered?.(true)}
              onPointerLeave={() => onEditHovered?.(false)}
              variant="ghost"
              size="xs"
              icon={<EditIcon position="relative" />}
            />
            {value && onDelete ? (
              <DeleteButton itemName={field.placeholder} onDelete={onDelete} />
            ) : null}
          </HStack>
        )}
      </Flex>
    </ThemeContext.Provider>
  )
}

export const EditableRow = ({
  label,
  stackProps,
  index,
  small,
  labelProps,
  ...props
}: EditableProps & {
  label: string
  small?: boolean
  stackProps?: StackProps
  labelProps?: TextProps
}) => {
  const [editHovered, setEditHovered] = useState(false)
  return (
    <HStack
      sx={
        index !== undefined
          ? {
              ':nth-of-type(2n)': {
                background: 'rgb(245,245,245)',
              },
            }
          : undefined
      }
      w="100%"
      alignItems="flex-start"
      spacing={1}
      py={small ? 0 : 1}
      px={small ? 1.5 : 2}
      transition="all 300ms"
      borderRadius={6}
      border={`1px dashed ${editHovered ? `${colors.green.hex}ff` : `${colors.green.hex}00`} `}
      {...stackProps}>
      <DataLabel pl={1} pt="1px" {...labelProps}>
        {label}
      </DataLabel>
      <Editable
        size="sm"
        onEditHovered={setEditHovered}
        style={{ flex: 1, minWidth: 0 }}
        dataCellProps={{
          pt: 1,
          lineHeight: props.field.type === FieldTypes.TEXTAREA ? 1.3 : 1,
          whiteSpace: props.field.type === FieldTypes.TEXTAREA ? 'pre-wrap' : 'nowrap',
          fontSize: '0.9rem',
        }}
        {...props}
      />
    </HStack>
  )
}

export default Input
